import React, { useEffect, useState } from 'react';
import { PhoneOutlined } from '@ant-design/icons';
import { NdoeImage } from '../../components/ndoe';
import WizardStepper from './WizardStepper';
import './deliveryWizard.css';

// Un échange à livrer a DEUX dénouements possibles ; le livreur choisit en 1re étape :
//  - 'echange' : flux normal — remettre la NEUVE → reprendre l'ANCIENNE → valider
//                (endpoint `echange/:id/executer`).
//  - 'garde'   : la cliente RENONCE et GARDE/achète l'ANCIENNE paire ; la neuve n'est PAS
//                remise (retour magasin). Encaissement + confirmation obligatoires
//                (endpoint `echange/:id/garder-ancienne`).
const STEPS_ECHANGE = [
  { key: 'remise', label: 'Remise' },
  { key: 'reprise', label: 'Reprise' },
  { key: 'recap', label: 'Valider' },
];
const STEPS_GARDE = [
  { key: 'garde', label: 'Encaisser' },
  { key: 'garde-recap', label: 'Valider' },
];

export default function ExchangeWizard({
  exchange, onClose, onSubmit, onKeepOriginal, saving, error, clearError, normalizePhoneCd,
}) {
  // L'option « garde » n'est proposée que si la paire d'origine est reliée ET encore en
  // attente d'échange (flag serveur `origine_disponible`). Le endpoint reste l'autorité.
  const canKeep = Number(exchange.origine_disponible) === 1;
  const [etape, setEtape] = useState('choix');
  const [payment, setPayment] = useState('');
  const [comment, setComment] = useState('');
  const [reprise, setReprise] = useState(false);
  useEffect(() => {
    setEtape('choix'); setPayment(''); setComment(''); setReprise(false);
  }, [exchange.id_detail_livraison]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (error) clearError?.(); }, [etape]);

  const avarie = Number(exchange.avarie_ancienne) === 1;
  const neuve = `${exchange.produit_neuf} · P${exchange.taille_neuf ?? '?'}${exchange.couleur_neuf ? ` · ${exchange.couleur_neuf}` : ''}`;
  const ancienne = exchange.produit_ancien
    ? `${exchange.produit_ancien}${exchange.taille_ancien ? ` · P${exchange.taille_ancien}` : ''}${exchange.couleur_ancien ? ` · ${exchange.couleur_ancien}` : ''}`
    : 'Ancienne paire';

  const Entete = (
    <section className="dw-client">
      <span className="dw-eyebrow">Échange · Commande #{exchange.id_commande}</span>
      <h2 className="dw-name">{exchange.nom_client}</h2>
      {exchange.telephone_client && (
        <a className="dw-call" href={`tel:+${normalizePhoneCd(exchange.telephone_client)}`}><PhoneOutlined /> Appeler</a>
      )}
    </section>
  );

  const ErrCta = () => (error ? <p className="dw-error" role="alert">{error}</p> : null);

  // ── Étape 0 : choix du dénouement ──────────────────────────────────────────
  if (etape === 'choix') {
    return (
      <main className="dw">
        <button type="button" className="dw-back" onClick={onClose}>← Échanges à livrer</button>
        {Entete}
        <div className="dw-step">
          <h3 className="dw-q center">Comment ça se passe avec {exchange.nom_client || 'la cliente'} ?</h3>
          <div className="dw-outcome">
            <button type="button" className="dw-outcome__card" onClick={() => setEtape('remise')}>
              <span className="dw-outcome__ic">🔄</span>
              <b>Échange normal</b>
              <small>Je remets la nouvelle paire et je reprends l’ancienne.</small>
            </button>
            <button type="button" className={`dw-outcome__card${canKeep ? '' : ' is-off'}`}
              disabled={!canKeep} onClick={() => canKeep && setEtape('garde')}>
              <span className="dw-outcome__ic">✋</span>
              <b>La cliente garde l’ancienne</b>
              <small>{canKeep
                ? 'Elle renonce à l’échange et achète l’ancienne paire ; je rapporte la neuve au magasin.'
                : 'Indisponible pour cet échange (paire d’origine non reliée).'}</small>
            </button>
          </div>
        </div>
      </main>
    );
  }

  // ── Branche GARDE — encaissement + confirmation ────────────────────────────
  if (etape === 'garde') {
    return (
      <main className="dw">
        <button type="button" className="dw-back" onClick={() => setEtape('choix')}>← Dénouement</button>
        {Entete}
        <div className="dw-step">
          <WizardStepper steps={STEPS_GARDE} current="garde" />
          <div className="dw-take">
            <span className="dw-take__ic">✋</span>
            <div className="dw-take__t"><b>{ancienne}</b><small>La cliente garde et achète cette paire.</small></div>
          </div>
          <label className="dw-confirm">
            <input type="checkbox" checked={reprise} onChange={(e) => setReprise(e.target.checked)} />
            <span>Je confirme : la <b>nouvelle paire</b> ({neuve}) n’est <b>pas remise</b> — je la rapporte au magasin.</span>
          </label>
          <label className="dw-field">
            <span>Encaissement (facultatif)</span>
            <input type="number" inputMode="numeric" min="0" step="1" value={payment}
              onChange={(e) => setPayment(e.target.value)} placeholder="Montant reçu de la cliente" />
          </label>
          <label className="dw-field">
            <span>Commentaire (facultatif)</span>
            <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)}
              placeholder="Note pour la secrétaire…" />
          </label>
        </div>
        <div className="dw-cta">
          <ErrCta />
          <button type="button" className="dw-btn primary" disabled={!reprise}
            onClick={() => setEtape('garde-recap')}>Continuer →</button>
        </div>
      </main>
    );
  }

  if (etape === 'garde-recap') {
    return (
      <main className="dw">
        <button type="button" className="dw-back" onClick={() => setEtape('garde')}>← Encaissement</button>
        {Entete}
        <div className="dw-step">
          <WizardStepper steps={STEPS_GARDE} current="garde-recap" />
          <h3 className="dw-q">On valide</h3>
          <div className="dw-recap">
            <div className="dw-recap__line">
              <span className="dw-recap__dot tone-ok" />
              <span className="dw-recap__t">Vendue à la cliente : {ancienne}</span>
              <b className="dw-recap__b">✓</b>
            </div>
            <div className="dw-recap__line">
              <span className="dw-recap__dot tone-ret" />
              <span className="dw-recap__t">Rapportée au magasin : {neuve}</span>
              <b className="dw-recap__b">↩︎</b>
            </div>
            {Number(payment) > 0 && (
              <div className="dw-recap__line">
                <span className="dw-recap__dot tone-report" />
                <span className="dw-recap__t">Encaissé</span>
                <b className="dw-recap__b">{payment}</b>
              </div>
            )}
          </div>
          <p className="dw-hint">L’échange sera <b>annulé</b> ; l’ancienne paire devient une <b>vente</b>. La nouvelle paire n’est pas décrémentée (elle repart au magasin).</p>
        </div>
        <div className="dw-cta">
          <ErrCta />
          <button type="button" className="dw-btn primary" disabled={saving}
            onClick={() => onKeepOriginal({ payment, comment, confirmationReprise: reprise })}>
            {saving ? 'Validation…' : '✓ Valider'}</button>
        </div>
      </main>
    );
  }

  // ── Branche ÉCHANGE normal — Étape 1 : remettre la nouvelle paire ───────────
  if (etape === 'remise') {
    return (
      <main className="dw">
        <button type="button" className="dw-back" onClick={() => setEtape('choix')}>← Dénouement</button>
        {Entete}
        <div className="dw-step">
          <WizardStepper steps={STEPS_ECHANGE} current={etape} />
          <NdoeImage src={exchange.img_neuf} alt={exchange.produit_neuf} size="hero" preview={false} className="dw-hero" />
          <div className="dw-pname-c">{neuve}</div>
          <p className="dw-q center">Remets cette paire à {exchange.nom_client || 'la cliente'}.</p>
        </div>
        <div className="dw-cta">
          <ErrCta />
          <button type="button" className="dw-btn primary" onClick={() => setEtape('reprise')}>C’est remis →</button>
        </div>
      </main>
    );
  }

  // ── Branche ÉCHANGE normal — Étape 2 : reprendre l'ancienne paire ───────────
  if (etape === 'reprise') {
    return (
      <main className="dw">
        <button type="button" className="dw-back" onClick={() => setEtape('remise')}>← À remettre</button>
        {Entete}
        <div className="dw-step">
          <WizardStepper steps={STEPS_ECHANGE} current={etape} />
          <div className={`dw-take${avarie ? ' is-avarie' : ''}`}>
            <span className="dw-take__ic">↩️</span>
            <div className="dw-take__t"><b>{ancienne}</b><small>Reprends l’ancienne paire auprès de la cliente.</small></div>
          </div>
          {avarie && (
            <p className="dw-avarie" role="note">⚠ Ancienne paire déclarée <b>défectueuse</b> — elle part en <b>avarie</b> (ne pas remettre en stock).</p>
          )}
        </div>
        <div className="dw-cta">
          <ErrCta />
          <button type="button" className="dw-btn primary" onClick={() => setEtape('recap')}>C’est repris →</button>
        </div>
      </main>
    );
  }

  // ── Branche ÉCHANGE normal — Étape 3 : valider ─────────────────────────────
  return (
    <main className="dw">
      <button type="button" className="dw-back" onClick={() => setEtape('reprise')}>← À reprendre</button>
      {Entete}
      <div className="dw-step">
        <WizardStepper steps={STEPS_ECHANGE} current="recap" />
        <h3 className="dw-q">On valide l’échange</h3>
        <div className="dw-recap">
          <div className="dw-recap__line">
            <span className="dw-recap__dot tone-ok" />
            <span className="dw-recap__t">Remis : {neuve}</span>
            <b className="dw-recap__b">✓</b>
          </div>
          <div className="dw-recap__line">
            <span className={`dw-recap__dot ${avarie ? 'tone-ret' : 'tone-report'}`} />
            <span className="dw-recap__t">Repris : {ancienne}</span>
            <b className="dw-recap__b">{avarie ? 'Avarie' : '✓'}</b>
          </div>
        </div>
        <p className="dw-hint">Une fois validé, la nouvelle paire sort du stock livreur et l’ancienne {avarie ? 'part en avarie' : 'revient au magasin'}.</p>
      </div>
      <div className="dw-cta">
        <ErrCta />
        <button type="button" className="dw-btn primary" disabled={saving}
          onClick={() => onSubmit()}>{saving ? 'Validation…' : '✓ Valider l’échange'}</button>
      </div>
    </main>
  );
}
